import type { AdAccount } from '@/shared/types/entities';

/**
 * Whether the organizer has to re-run OAuth for this account.
 *
 * Shared so the accounts page, the sidebar badge and the campaign wizard can
 * never disagree about which accounts are broken — a card showing a reconnect
 * prompt while the nav stays clean is worse than either signal alone.
 *
 * Two things force a reconnect:
 * - the token is expired or about to expire, and
 * - the token predates a permission the product now requires. That one is
 *   invisible to expiry: the credential keeps working for everything it was
 *   originally granted, and only the feature needing the new permission
 *   breaks. Catching it here means the organizer is told on the accounts page
 *   instead of inside the wizard step that fails.
 *
 * Managed accounts are excluded: their credentials belong to events.com, so
 * the organizer has nothing to reconnect and the prompt would be a dead end.
 */
export function needsReconnect(account: AdAccount): boolean {
  if (account.isManaged) return false;
  if (hasMissingScopes(account)) return true;
  return account.tokenStatus === 'expired' || account.tokenStatus === 'expiring';
}

/** Whether the account is missing a permission the product now requires. */
export function hasMissingScopes(account: AdAccount): boolean {
  return !account.isManaged && (account.missingScopes?.length ?? 0) > 0;
}

/**
 * Accounts the organizer must reconnect, worst-first: a broken connection
 * outranks a missing permission, which outranks one merely expiring.
 */
export function accountsNeedingReconnect(accounts: AdAccount[] | undefined): AdAccount[] {
  const severity = (account: AdAccount): number => {
    if (account.tokenStatus === 'expired') return 0;
    if (hasMissingScopes(account)) return 1;
    return 2;
  };
  return (accounts ?? []).filter(needsReconnect).sort((a, b) => severity(a) - severity(b));
}
